import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { 
    BookText,
    DollarSign,
    Calendar,
    Clock,
    Building,
    CheckCircle,
    AlertCircle
} from 'lucide-react';
import '../create_user/CreateUserPage.css';
import DashboardBase from "../DashboardBase";
import CurrencySearch from '../../components/CurrencySearch';
import FundSearch from '../../components/FundManagerSearch';
import moment from 'moment'

const COMPOUNDING_FREQUENCIES = [
    { value: 'Daily', label: 'Daily' },
    // { value: 'WEEKLY', label: 'Weekly' },
    { value: 'Monthly', label: 'Monthly' },
    // { value: 'QUARTERLY', label: 'Quarterly' },
    // { value: 'ANNUALLY', label: 'Annually' }
];

const DISTRIBUTION_FREQUENCIES = [
    { value: 'Monthly', label: 'Monthly' },
    // { value: 'QUARTERLY', label: 'Quarterly' },
    // { value: 'SEMI_ANNUALLY', label: 'Semi-Annually' },
    { value: 'Yearly', label: 'Annually' }
];

const ASSET_TYPES = [
    { value: 'Fixed Deposits', label: 'Fixed Deposits' },
    { value: 'Government Securities', label: 'Government Securities' },
    { value: 'Cash', label: 'Cash' },
    { value: 'Cash & Equivalents', label: 'Cash & Equivalents' },
    { value: 'Commericial Paper', label: 'Commericial Paper' },
    { value: 'Corporate Debt', label: 'Corporate Debt' },
    { value: 'Corporate Bonds', label: 'Corporate Bonds' },
    { value: 'Call Deposits', label: 'Call Deposits' },
];

const CreateFundPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [futureRates, setFutureRates] = useState([]);
    const [pastRates, setPastRates] = useState([]);
    const [assetAllocations, setAssetAllocations] = useState([]);
    const [newAsset, setNewAsset] = useState({ asset_type: '', percentage: '' });
    const [allocationError, setAllocationError] = useState('');

    const generateNextSixMonths = () => {
        const months = [];
        const today = new Date();
        const year = today.getFullYear();
        const thisMonth = today.getMonth();
        for (let i = 1; i <= 6; i++) {
            // Set start date to first day of month
            const startDate = new Date(year, today.getMonth() + i, 1);
            // Set end date to last day of month
            const endDate = new Date(year, today.getMonth() + (i + 1), 0);
            
            months.push({
                rate_start_date: moment(startDate).format('YYYY-MM-DD'), // Format: YYYY-MM-DD
                rate_end_date: moment(endDate).format('YYYY-MM-DD'), // Format: YYYY-MM-DD
                interest_rate: 10.0,
                active: true
            });
        }
        return months;
    };
    
    const generatePastThreeYears = () => {
        const years = [];
        const year = new Date().getFullYear();
        for (let i = 1; i < 4; i++) {
            years.push({
                rate_start_date: moment(new Date(year - i, 0, 1)).format('YYYY-MM-DD'),
                interest_rate: 10.0,
                active: true // Add this field
            });
        }
        return years;
    };

    const toggleFutureRate = (index) => {
        const newRates = [...futureRates];
        newRates[index].active = !newRates[index].active;
        newRates[index].interest_rate = 0; // Clear the rate when deactivating
        setFutureRates(newRates);
    };
    
    const togglePastRate = (index) => {
        const newRates = [...pastRates];
        newRates[index].active = !newRates[index].active;
        newRates[index].interest_rate = 0; // Clear the rate when deactivating
        setPastRates(newRates);
    };
    
    const [formData, setFormData] = useState({
        name: '',
        symbol: '',
        currency_code: 'USD',
        min_value: '',
        min_addition_value: '',
        compounding_frequency: 'Monthly',
        distribution_frequency: 'Yearly',
        lock_in_period_days: '',
        withdrawl_turnaround_days: '',
        truster: '',
        custodian: '',
        auditor: '',
        fund_manager_id: 0,
        management_fee: 2.00,  // Default value
        initial_fee: 0,       // Default value
        bench_mark: '',        // New field
        asset_allocations: []
    });

    const [validation, setValidation] = useState({
        name: true,
        symbol: true,
        min_value: true,
        min_addition_value: true,
        lock_in_period_days: true,
        withdrawl_turnaround_days: true,
        truster: true,
        custodian: true,
        auditor: true
    });

    const validateForm = () => {
        const newValidation = {
            name: formData.name.length >= 2,
            symbol: /^[A-Z0-9]{2,8}$/.test(formData.symbol),
            min_value: Number(formData.min_value) > 0,
            min_addition_value: Number(formData.min_addition_value) > 0,
            lock_in_period_days: Number(formData.lock_in_period_days) >= 0,
            withdrawl_turnaround_days: Number(formData.withdrawl_turnaround_days) >= 0,
            truster: formData.truster.length >= 2,
            custodian: formData.custodian.length >= 2,
            auditor: formData.auditor.length >= 2
        };

        setValidation(newValidation);
        return Object.values(newValidation).every(Boolean);
    };

    useEffect(() => {
        setFutureRates(generateNextSixMonths());
        setPastRates(generatePastThreeYears());
    }, []);
    
    // Add these handler functions
    const handleFutureRateChange = (index, value) => {
        const newRates = [...futureRates];
        newRates[index].interest_rate = value;
        setFutureRates(newRates);
    };
    
    const handlePastRateChange = (index, value) => {
        const newRates = [...pastRates];
        newRates[index].interest_rate = value;
        setPastRates(newRates);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError(null);
    };

    const handleAddAssetAllocation = () => {
        if (!newAsset.asset || !newAsset.allocation) {
            setAllocationError('Please fill in both asset type and percentage');
            return;
        }
    
        const totalCurrentAllocation = assetAllocations.reduce((sum, asset) => 
            sum + Number(asset.allocation), 0);
        const newTotal = totalCurrentAllocation + Number(newAsset.allocation);
    
        if (newTotal > 100) {
            setAllocationError('Total allocation cannot exceed 100%');
            return;
        }
    
        setAssetAllocations([...assetAllocations, newAsset]);
        setNewAsset({ asset: '', allocation: '' });
        setAllocationError('');
    };
    
    const handleRemoveAssetAllocation = (index) => {
        setAssetAllocations(assetAllocations.filter((_, i) => i !== index));
    };
    
    const getTotalAllocation = () => {
        return assetAllocations.reduce((sum, asset) => sum + Number(asset.allocation), 0);
    };    

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            setError('Please correct the validation errors.');
            return;
        }

        if (getTotalAllocation() !== 100) {
            setError('Total asset allocation must equal 100%');
            return;
        }

        setLoading(true); 
        try {
            const submitData = {
                ...formData,
                lock_in_period_days: Number(formData.lock_in_period_days) !== 0 ? Number(formData.lock_in_period_days) : 0,
                withdrawl_turnaround_days: Number(formData.withdrawl_turnaround_days) !== 0 ? Number(formData.withdrawl_turnaround_days) : 0,
                min_value: Number(formData.min_value) !== 0 ? Number(formData.min_value) : 0,
                min_addition_value: Number(formData.min_addition_value) !== 0 ? Number(formData.min_addition_value) : 0,
                management_fee: Number(formData.management_fee) !== 0 ? Number(formData.management_fee) : 0,
                initial_fee: Number(formData.initial_fee) !== 0 ? Number(formData.initial_fee) : 0,
                asset_allocation: assetAllocations.map(allocation => ({
                    ...allocation,
                    allocation: Number(allocation.allocation) / 100
                })),
                future_rates: futureRates
                .filter(rate => rate.active) // Filter only active rates
                .map(rate => ({
                    ...rate,
                    interest_rate: Number(rate.interest_rate) !== 0 
                        ? Number(rate.interest_rate) / 100 
                        : 0
                })),
                past_rates: pastRates
                .filter(rate => rate.active) // Filter only active rates
                .map(rate => ({
                    ...rate,
                    interest_rate: Number(rate.interest_rate) !== 0 
                        ? Number(rate.interest_rate) / 100 
                        : 0
                }))
            };
            console.log(JSON.stringify(submitData));

            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/funds`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(submitData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to create fund');
            }

            setSuccess(true);
            setTimeout(() => {
                navigate('/funds');
            }, 2000);

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFundSelect = (fund) => {
        // Call your fetchTransactions with the selected fund
        setFormData(prev => ({
            ...prev,
            ["fund_manager_id"]: fund.fund_manager_id
        }));
    };

    const handleCurrencySelect = (icurrency) => {
        // Call your fetchTransactions with the selected fund
        setFormData(prev => ({
            ...prev,
            ["currency_code"]: icurrency.currency_code
        }));
    };

    return (
        <DashboardBase
            page="home"
            content={
                <div className="create-user-page">
                    <div className="dashboard-page">
                        <div className="dashboard-header">
                            <h1>Create New Fund</h1>
                        </div>

                        <div className="content-card">
                            {success ? (
                                <div className="success-message">
                                    <CheckCircle className="success-icon" />
                                    <p>Fund created successfully! Redirecting...</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="create-fund-form">
                                    {error && (
                                        <div className="error-message">
                                            <AlertCircle className="error-icon" />
                                            {error}
                                        </div>
                                    )}

                                   <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="currency_code">Fund Manager</label>
                                            <div className="input-wrapper">
                                                <DollarSign className="input-icon" />
                                                <FundSearch onFundSelect={handleFundSelect} />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="currency_code">Currency</label>
                                            <div className="input-wrapper">
                                                <DollarSign className="input-icon" />
                                                <CurrencySearch onCurrencySelect={handleCurrencySelect} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="name">Fund Name</label>
                                            <div className="input-wrapper">
                                                <BookText className="input-icon" />
                                                <input
                                                    type="text"
                                                    id="name"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    className={!validation.name ? 'invalid' : ''}
                                                    placeholder="Enter fund name"
                                                    required
                                                />
                                            </div>
                                            {!validation.name && (
                                                <span className="validation-message">
                                                    Name must be at least 2 characters
                                                </span>
                                            )}
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="symbol">Symbol</label>
                                            <div className="input-wrapper">
                                                <BookText className="input-icon" />
                                                <input
                                                    type="text"
                                                    id="symbol"
                                                    name="symbol"
                                                    value={formData.symbol}
                                                    onChange={handleChange}
                                                    className={!validation.symbol ? 'invalid' : ''}
                                                    placeholder="Enter fund symbol"
                                                    required
                                                />
                                            </div>
                                            {!validation.symbol && (
                                                <span className="validation-message">
                                                    Symbol must be 2-8 uppercase characters or numbers
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="min_value">Minimum Investment</label>
                                            <div className="input-wrapper">
                                                <DollarSign className="input-icon" />
                                                <input
                                                    type="number"
                                                    id="min_value"
                                                    name="min_value"
                                                    value={formData.min_value}
                                                    onChange={handleChange}
                                                    className={!validation.min_value ? 'invalid' : ''}
                                                    placeholder="Enter minimum investment"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="min_addition_value">Minimum Additional Investment</label>
                                            <div className="input-wrapper">
                                                <DollarSign className="input-icon" />
                                                <input
                                                    type="number"
                                                    id="min_addition_value"
                                                    name="min_addition_value"
                                                    value={formData.min_addition_value}
                                                    onChange={handleChange}
                                                    className={!validation.min_addition_value ? 'invalid' : ''}
                                                    placeholder="Enter minimum additional investment"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="distribution_frequency">Distribution Frequency</label>
                                            <div className="input-wrapper">
                                                <Calendar className="input-icon" />
                                                <select
                                                    id="distribution_frequency"
                                                    name="distribution_frequency"
                                                    value={formData.distribution_frequency}
                                                    onChange={handleChange}
                                                    required
                                                >
                                                    {DISTRIBUTION_FREQUENCIES.map(freq => (
                                                        <option key={freq.value} value={freq.value}>
                                                            {freq.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="compounding_frequency">Compounding Frequency</label>
                                            <div className="input-wrapper">
                                                <Calendar className="input-icon" />
                                                <select
                                                    id="compounding_frequency"
                                                    name="compounding_frequency"
                                                    value={formData.compounding_frequency}
                                                    onChange={handleChange}
                                                    required
                                                >
                                                    {COMPOUNDING_FREQUENCIES.map(freq => (
                                                        <option key={freq.value} value={freq.value}>
                                                            {freq.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="lock_in_period_days">Lock-in Period (Days)</label>
                                            <div className="input-wrapper">
                                                <Clock className="input-icon" />
                                                <input
                                                    type="number"
                                                    id="lock_in_period_days"
                                                    name="lock_in_period_days"
                                                    value={formData.lock_in_period_days}
                                                    onChange={handleChange}
                                                    className={!validation.lock_in_period_days ? 'invalid' : ''}
                                                    placeholder="Enter lock-in period in days"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="withdrawl_turnaround_days">Withdrawal Turnaround (Days)</label>
                                            <div className="input-wrapper">
                                                <Clock className="input-icon" />
                                                <input
                                                    type="number"
                                                    id="withdrawl_turnaround_days"
                                                    name="withdrawl_turnaround_days"
                                                    value={formData.withdrawl_turnaround_days}
                                                    onChange={handleChange}
                                                    className={!validation.withdrawl_turnaround_days ? 'invalid' : ''}
                                                    placeholder="Enter withdrawal turnaround days"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="truster">Truster</label>
                                            <div className="input-wrapper">
                                                <Building className="input-icon" />
                                                <input
                                                    type="text"
                                                    id="truster"
                                                    name="truster"
                                                    value={formData.truster}
                                                    onChange={handleChange}
                                                    className={!validation.truster ? 'invalid' : ''}
                                                    placeholder="Enter truster name"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="custodian">Custodian</label>
                                            <div className="input-wrapper">
                                                <Building className="input-icon" />
                                                <input
                                                    type="text"
                                                    id="custodian"
                                                    name="custodian"
                                                    value={formData.custodian}
                                                    onChange={handleChange}
                                                    className={!validation.custodian ? 'invalid' : ''}
                                                    placeholder="Enter custodian name"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="auditor">Auditor</label>
                                            <div className="input-wrapper">
                                                <Building className="input-icon" />
                                                <input
                                                    type="text"
                                                    id="auditor"
                                                    name="auditor"
                                                    value={formData.auditor}
                                                    onChange={handleChange}
                                                    className={!validation.auditor ? 'invalid' : ''}
                                                    placeholder="Enter auditor name"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="benchmark">Benchmark</label>
                                            <div className="input-wrapper">
                                                <BookText className="input-icon" />
                                                <input
                                                    type="text"
                                                    id="benchmark"
                                                    name="bench_mark"
                                                    value={formData.bench_mark}
                                                    onChange={handleChange}
                                                    placeholder="Enter benchmark"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Add after your existing form groups */}
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="management_fee">Management Fee (%)</label>
                                            <div className="input-wrapper">
                                                <DollarSign className="input-icon" />
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    id="management_fee"
                                                    name="management_fee"
                                                    value={formData.management_fee}
                                                    onChange={handleChange}
                                                    placeholder="Enter management fee"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="initial_fee">Initial Fee (%)</label>
                                            <div className="input-wrapper">
                                                <DollarSign className="input-icon" />
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    id="initial_fee"
                                                    name="initial_fee"
                                                    value={formData.initial_fee}
                                                    onChange={handleChange}
                                                    placeholder="Enter initial fee"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="asset-allocation-section">
                                        <h3>Asset Allocation</h3>
                                        <div className="current-allocations">
                                            {assetAllocations.map((allocation, index) => (
                                                <div key={index} className="allocation-item">
                                                    <span>{allocation.asset}: {allocation.allocation}%</span>
                                                    <button 
                                                        type="button" 
                                                        onClick={() => handleRemoveAssetAllocation(index)}
                                                        className="remove-allocation-btn"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            ))}
                                            <div className="total-allocation">
                                                Total Allocation: {getTotalAllocation()}%
                                            </div>
                                        </div>

                                        <div className="add-allocation-form">
                                            <div className="form-row">
                                                <div className="form-group">
                                                    <select
                                                        value={newAsset.asset}
                                                        onChange={(e) => setNewAsset({...newAsset, asset: e.target.value})}
                                                    >
                                                        <option value="">Select Asset Type</option>
                                                        {ASSET_TYPES.map(type => (
                                                            <option key={type.value} value={type.value}>
                                                                {type.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="form-group">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        value={newAsset.allocation}
                                                        onChange={(e) => setNewAsset({...newAsset, allocation: e.target.value})}
                                                        placeholder="Percentage"
                                                    />
                                                </div>
                                                <button 
                                                    type="button"
                                                    onClick={handleAddAssetAllocation}
                                                    className="add-allocation-btn"
                                                >
                                                    Add Asset
                                                </button>
                                            </div>
                                            {allocationError && (
                                                <div className="allocation-error">
                                                    {allocationError}
                                                </div>
                                            )}
                                        </div>
                                    </div>


                                        <div className="rates-section">
                                            <h3>Future Fund Rates</h3>
                                            <div className="rates-grid">
                                                {futureRates.map((rate, index) => (
                                                    <div key={rate.rate_start_date} className={`rate-input-group ${!rate.active ? 'inactive' : ''}`}>
                                                        <div className="rate-header">
                                                            <label>{new Date(rate.rate_start_date).toLocaleString('default', { month: 'long', year: 'numeric' })}</label>
                                                            <button 
                                                                type="button"
                                                                className="toggle-rate-btn"
                                                                onClick={() => toggleFutureRate(index)}
                                                            >
                                                                {rate.active ? 'Remove' : 'Add'}
                                                            </button>
                                                        </div>
                                                        {rate.active && (
                                                            <div className="input-wrapper">
                                                                <DollarSign className="input-icon" />
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={rate.interest_rate}
                                                                    onChange={(e) => handleFutureRateChange(index, e.target.value)}
                                                                    placeholder="Enter rate"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>

                                            <h3>Past Fund Rates</h3>
                                            <div className="rates-grid">
                                                {pastRates.map((rate, index) => (
                                                    <div key={rate.rate_start_date} className={`rate-input-group ${!rate.active ? 'inactive' : ''}`}>
                                                        <div className="rate-header">
                                                        <label>{new Date(rate.rate_start_date).toLocaleString('default', { month: 'long', year: 'numeric' })}</label>
                                                        <button 
                                                                type="button"
                                                                className="toggle-rate-btn"
                                                                onClick={() => togglePastRate(index)}
                                                            >
                                                                {rate.active ? 'Remove' : 'Add'}
                                                            </button>
                                                        </div>
                                                        {rate.active && (
                                                            <div className="input-wrapper">
                                                                <DollarSign className="input-icon" />
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={rate.interest_rate}
                                                                    onChange={(e) => handlePastRateChange(index, e.target.value)}
                                                                    placeholder="Enter rate"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    <div className="form-actions">
                                        <button 
                                            type="button" 
                                            className="button secondary"
                                            onClick={() => navigate('/funds')}
                                        >
                                            Cancel
                                        </button>
                                        <button 
                                            type="submit" 
                                            className="button primary"
                                            disabled={loading}
                                        >
                                            {loading ? 'Creating...' : 'Create Fund'}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            }
        />
    );
};

export default CreateFundPage;
